"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactezUnExpert() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="241332" id="book-your-session" className="relative w-full py-24 bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="font-default text-4xl md:text-5xl font-bold text-foreground mb-4 tracking-tight">
            Book Your Session
          </h2>
          <p className="font-default text-lg text-muted-foreground">
            Ready to capture your sound? Tell us about your project and let's create something legendary.
          </p>
        </div>

        <div ref={ref} className={`max-w-3xl mx-auto transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Card className="border-border/40 shadow-2xl shadow-black/50 bg-card/40 backdrop-blur-xl rounded-3xl">
            <CardContent className="p-6 md:p-10">
              <FormProvider formKey="session_booking_form_contact" formId="session_booking_form_contact" sectionName="Contactez un Expert" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              subject: "",
              message: "",
              consent: false
            }} validationRules={{
              full_name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              subject: {
                required: true
              },
              message: {
                required: true,
                minLength: 20,
                maxLength: 2000
              },
              consent: {
                required: true
              }
            }} submitText="Initialize Session" successMessage="Signal received. Our engineers are reviewing your project details and will reach out shortly." errorMessage="Transmission error. Please check your connection and try again.">
                <ContactFormInner />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/20 rounded-full flex items-center justify-center mb-2 border border-primary/50">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="font-default text-2xl font-bold text-foreground">Session Initialized</h3>
        <p className="text-muted-foreground max-w-md font-default">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-full border-primary/50 text-primary hover:bg-primary/10">
          Book Another Session
        </Button>
      </div>;
  }
  const labelClasses = "text-xs uppercase tracking-widest font-mono font-semibold text-primary/80";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Artist or Band name is required"
      }} render={({
        field
      }) => <FormItem className="col-span-1">
              <FormLabel className={labelClasses}>Artist / Band Name</FormLabel>
              <FormControl>
                <Input placeholder="PulseWave Collective" className="bg-background/50 border-border focus:border-primary focus:ring-primary/20 rounded-xl" {...field} />
              </FormControl>
              <FormDescription className="text-xs font-mono opacity-60">Enter your stage or group name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="col-span-1">
              <FormLabel className={labelClasses}>Contact Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="bg-background/50 border-border focus:border-primary focus:ring-primary/20 rounded-xl" {...field} />
              </FormControl>
              <FormDescription className="text-xs font-mono opacity-60">Optional – for direct coordination.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
              <FormLabel className={labelClasses}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="artist@pulsewave.studio" className="bg-background/50 border-border focus:border-primary focus:ring-primary/20 rounded-xl" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="subject" rules={{
        required: "Please select a project type"
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
              <FormLabel className={labelClasses}>Project Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-background/50 border-border focus:border-primary rounded-xl">
                    <SelectValue placeholder="Select your service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-card border-border">
                  {["Full Band Recording", "Vocal Tracking & Production", "Mixing & Mastering", "Podcast / Voiceover"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="message" rules={{
        required: "Project details are required",
        minLength: {
          value: 20,
          message: "Please provide more details about your vision"
        }
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
              <FormLabel className={labelClasses}>Project Vision</FormLabel>
              <FormControl>
                <Textarea placeholder="Describe the sound you're chasing, number of tracks, or specific gear requirements..." className="min-h-[150px] resize-y bg-background/50 border-border focus:border-primary focus:ring-primary/20 rounded-xl" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="consent" rules={{
        required: "You must accept the terms"
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2 flex flex-row items-start space-x-3 space-y-0 rounded-2xl border border-border/40 bg-accent/10 p-4">
              <FormControl>
                <input type="checkbox" checked={field.value} onChange={field.onChange} className="mt-1 size-4 shrink-0 rounded-sm border-primary text-primary focus:ring-primary accent-primary cursor-pointer" />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-medium text-foreground cursor-pointer font-default">
                  I agree to the studio terms and privacy policy.
                </FormLabel>
                <FormDescription className="text-xs font-mono opacity-60">
                  Your creative data is secure with us.
                </FormDescription>
                <FormMessage />
              </div>
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive font-medium font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 font-default font-bold text-lg tracking-wider rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:shadow-xl hover:shadow-primary/40 hover:-translate-y-0.5">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing Signal...
          </> : "Initialize Session"}
      </Button>
    </Form>;
}